import flat from 'flat';
import { difference, flow, get, isEmpty, omit, omitBy } from 'lodash';
import URI from 'urijs';
import { windowWrapper, appQueryParameters, url as urlUtils } from '@clubmed/utils';
import { getTldFromLocale, getDefaultLocaleForTld } from '@clubmed/domains-service';
import { STATIC_PAGE_TYPES } from 'Tracking/constants';
import { formatTimestampDate } from 'Utils/date/dateUtils';
import { AUTHORIZED_QUERY_PARAMS, HASH_PARAMS_DELIMITER, QUERY_DATE_FORMAT } from './constants';
export const BASE_URL = 'https://www.clubmed.com';
export const getLocationSearch = (
  location:
    | RouterLocation<any, any>
    | {
        queryParams: ParsedUrlQuery;
      },
): string => new URI().search(location.queryParams).search();
export const convertLocationToURI = (location: RouterLocation<any, any>): URI =>
  new URI()
    .origin(location.origin || (typeof window !== 'undefined' ? windowWrapper.getOrigin() : ''))
    .pathname(location.pathname) // @ts-expect-error TODO(flow-migration): search? Probably to remove once Next.js is set up
    .search(location.queryParams || location.search)
    .hash(location.hash || '');
// Sorry, this was necessary to fix the Quotation Form breaking a lot of tests!
// Must be fixed with Next.js
export const convertQuotationFormLocationToHref = (location: any = {}): string =>
  new URI()
    .pathname(location.pathname)
    .search(location.queryParams || location.search)
    .toString();
export const convertLocationToHref = (location: any): string =>
  `${location.pathname}${location.search}${location.hash}`;
export const convertUrlToOrigin = (url: string): string => new URI(url).origin();
export function getHashParams(uri: any = '') {
  const hash = new URI(uri).hash();
  const hashParamsDelimiterPos = hash.indexOf(HASH_PARAMS_DELIMITER);
  const hashParamsQuery =
    hashParamsDelimiterPos !== -1
      ? hash.slice(hashParamsDelimiterPos + HASH_PARAMS_DELIMITER.length)
      : '';

  if (hashParamsQuery) {
    const hashPosInHashParamsQuery = hashParamsQuery.indexOf('#');
    return hashPosInHashParamsQuery !== -1
      ? URI.parseQuery(hashParamsQuery.slice(0, hashPosInHashParamsQuery))
      : URI.parseQuery(hashParamsQuery);
  }

  return {};
}
export const getOpenLayersFromLocation = (url: string, queryLayer: string): Array<number> => {
  const uri = new URI(url);
  const activeLayers = uri.query(true)[queryLayer] ?? '0_-1';
  const active = Array.isArray(activeLayers) ? activeLayers[0] : activeLayers;
  return active.split('_').map((index) => Number(index));
};
export function formatDateForHashParams(timestamp: any) {
  return formatTimestampDate(timestamp, QUERY_DATE_FORMAT);
}

function _removeEmptyAttributes(object) {
  return omitBy(object, isEmpty);
}

function _appendHashParamsToUrl(hashParams) {
  return (url) => {
    const hashParamsWithNoEmptyValues = _removeEmptyAttributes(hashParams);

    return !isEmpty(hashParamsWithNoEmptyValues)
      ? url.hash(`${HASH_PARAMS_DELIMITER}${URI.buildQuery(hashParamsWithNoEmptyValues)}`)
      : url;
  };
}

function _appendQueryParamsToUrl(queryParams) {
  return (url) => {
    const queryParamsWithNoEmptyValues = _removeEmptyAttributes(queryParams);

    return !isEmpty(queryParamsWithNoEmptyValues)
      ? url.setQuery(queryParamsWithNoEmptyValues)
      : url;
  };
}

export function generateUrlWithParams(url: any, params: any) {
  return flow([
    _appendQueryParamsToUrl(get(params, 'queryParams', {})),
    _appendHashParamsToUrl(get(params, 'hashParams', {})),
  ])(new URI(url)).toString();
}
export function generateUri(path: any = '', params: any) {
  return generateUrlWithParams(`${windowWrapper.getOrigin()}${path}`, params);
}
export function generateLocalizedHref(
  locale: any,
  resource: any,
  slug: any,
): {
  href: string;
  hrefLang: string;
  rel: string;
} {
  if (!locale || !resource || !slug) {
    return {};
  }

  const href = new URI(BASE_URL).path(`${resource}/${slug}`);
  const tld = getTldFromLocale(locale);

  if (!tld) {
    return {};
  }

  href.tld(tld);
  const defaultLocale = getDefaultLocaleForTld(tld);

  if (defaultLocale && defaultLocale !== locale) {
    href.search({
      locale,
    });
  }

  return {
    href: href.toString(),
    hrefLang: locale,
    rel: 'alternate',
  };
}

function flatObjectReducer(o, reducer) {
  return o && flat.unflatten(Object.keys(o).reduce((acc, key) => reducer(acc, key, o[key]), {}));
}

export function getQueryParameters(url: any) {
  url = url.query ? url : new URI(url);
  return url.query(true);
}
export function getSpecificQueryParam(queryParam: any) {
  if (typeof window !== 'undefined') {
    const location = windowWrapper.getLocation();
    const queryParamValue = get(getQueryParameters(location), queryParam);
    return queryParamValue;
  }

  return '';
}

function assignQueryParameters(previousUrl, nextUrl) {
  return {
    ...omit(getQueryParameters(previousUrl), Object.keys(appQueryParameters.ignore)),
    ...getQueryParameters(nextUrl),
  };
}

function filterQueryParameters(queryParameters, queryParameterNames) {
  const lowerParamNames = queryParameterNames.map((param) => param && param.toLowerCase());
  return flatObjectReducer(queryParameters, (filteredObject, key, value) => {
    if (lowerParamNames.indexOf(key.toLowerCase()) > -1) {
      filteredObject[key] = value;
    }

    return filteredObject;
  });
}

function getTargetQueryParameters(previousUrl, nextUrl) {
  return filterQueryParameters(
    assignQueryParameters(previousUrl, nextUrl),
    AUTHORIZED_QUERY_PARAMS,
  );
}

export function buildTargetUrlPreservingParams(previousUrl: any, nextUrlString: any, options: any) {
  const shouldPropagateHashParams = get(options, 'propagateHashParams', false);
  const nextUrl = new URI(nextUrlString);
  const targetUrl = nextUrl.query(getTargetQueryParameters(previousUrl, nextUrl));
  const targetWithHashParams = shouldPropagateHashParams
    ? targetUrl.hash(`${previousUrl.hash()}`)
    : targetUrl;
  return targetWithHashParams;
}
export function filterUnauthorizedParams(url: any, parametersToIgnore: any = []) {
  const uriUrl = new URI(url);
  const filteredQueryParams = filterQueryParameters(
    getQueryParameters(uriUrl),
    difference(AUTHORIZED_QUERY_PARAMS, parametersToIgnore),
  );
  return uriUrl.query(filteredQueryParams).toString();
}
const ALLOWED_CANONICAL_QUERY_PARAMETERS = [appQueryParameters.locale];
export function getPageCanonicalUrl(requestUrl: string, pageUrl: string = '/'): string {
  const requestURI = new URI(requestUrl);
  const pageURI = new URI(pageUrl);
  const urlPath = pageURI.path(true);
  const urlQuery = URI.buildQuery(
    Object.entries(requestURI.query(true))
      .filter(([key]) => ALLOWED_CANONICAL_QUERY_PARAMETERS.includes(key))
      .reduce((params, [key, value]) => ({ ...params, [key]: value }), {}),
  );
  const normalizedPath = urlUtils.normalizePath(urlPath, true);
  return URI.build({
    protocol: 'https',
    hostname: requestURI.hostname(),
    path: normalizedPath,
    query: urlUtils.normalizeQuery(urlQuery),
  });
}
export const formatLink = (
  href?: string,
  originalUrl: string,
  locale: string,
  pageType?: string,
  type?: string,
): string => {
  const origin =
    typeof window !== 'undefined' ? window.location.origin : new URI(originalUrl).origin();
  const url = new URL(href, origin);

  if ((href && !href.startsWith('/')) || !locale) {
    if (pageType === STATIC_PAGE_TYPES.PLE && type === 'mail') {
      url.searchParams.set('origin', STATIC_PAGE_TYPES.PLE);
    }

    return url.href.replace(origin, '');
  }

  url.searchParams.set('locale', locale);
  return url.href.replace(origin, '');
};
